// Copyright 2023 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { BindingEngine, Disposable } from "aurelia-framework";
import { PlotData } from "controls/plot/graph-plot/GraphPlot";
import { Range as IRange } from "datatypes/SoramaLocalApi";
import {
    NarrowbandSelectionMode,
    NarrowbandSelectionModes,
} from "modules/workflows/Analysis/Spectrum/SpectrumModuleModes";
import { FrequencySelectionStrategies } from "./FrequencySelectionStrategies";

export class SelectableRange implements IRange {
    constructor(from: number, to: number) {
        this.from = from;
        this.to = to;
        this.isSelected = false;
    }

    public from: number;
    public to: number;
    public isSelected = false;
}

/**
 * Service to select a frequency selection based on user-defined ranges and selection modes/strategies.
 */
@autoinject
export class FrequencySelectionService {
    // Depending on the range and narrow band mode, the frequency selection will change for all selected measurements.
    public selectedFrequency: number = null;

    // Needs to be readonly to preserve all the observers
    // Only use splice to change the array
    public ranges: SelectableRange[] = [];
    public get selectedRanges(): SelectableRange[] {
        return this.ranges.filter((r) => r.isSelected);
    }

    private selectionMode: NarrowbandSelectionMode = NarrowbandSelectionModes.maxBand;
    private modules: Map<string, PlotData[]> = new Map<string, PlotData[]>();
    private activeModuleId: string = null;

    constructor(private readonly bindingEngine: BindingEngine) {
        // Ensure _rangeSubscriptions is always an array.
        if (!Array.isArray(this._rangeSubscriptions)) {
            this._rangeSubscriptions = [];
        }

        // This gets triggered when the array of ranges is changed (not the properties of the ranges themselves)
        this.bindingEngine.collectionObserver(this.ranges).subscribe(() => this.rangeChanged());
    }

    // #Region Selected Frequencies Modules Management
    public setSelectionMode(mode: NarrowbandSelectionMode): void {
        this.selectionMode = mode;
        this.updateSelectedFrequency();
    }

    public registerModule(moduleId: string, spectra: PlotData[]): void {
        this.modules.set(moduleId, spectra);
        this.updateSelectedFrequency();
    }

    public unregisterModule(moduleId: string): void {
        this.modules.delete(moduleId);
        if (this.activeModuleId === moduleId) {
            this.activeModuleId = null;
        }
        this.updateSelectedFrequency();
    }

    public setActiveModule(moduleId: string): void {
        this.activeModuleId = moduleId;
        this.updateSelectedFrequency();
    }

    private updateSelectedFrequency(): void {
        if (this.selectedRanges.length === 0 || this.modules.size === 0) {
            this.selectedFrequency = null;
            return;
        }

        const spectra = this.activeModuleId
            ? this.modules.get(this.activeModuleId) || []
            : Array.from(this.modules.values()).flat();

        // Find the highest selected frequency from the selected ranges across the relevant spectra
        this.selectedFrequency = FrequencySelectionStrategies[this.selectionMode](spectra, this.ranges);
    }

    // #EndRegion

    // #Region Observers
    private _rangeObjectObservers: Map<SelectableRange, Disposable> = new Map<SelectableRange, Disposable>();

    private rangeChanged(): void {
        // Notify that the range array has changed (but range properties not necessarily, hence false)
        this.notifyRangeChanged(false);

        // Add observer for each property in each range
        // but first dispose old ones of all listeners when the ranges change
        this._rangeObjectObservers.forEach((disposable, range) => {
            if (!this.ranges.includes(range)) {
                disposable.dispose();
                this._rangeObjectObservers.delete(range);
            }
        });

        // Add listeners for all properties of all the ranges
        this.ranges.forEach((range) => {
            if (!this._rangeObjectObservers.has(range)) {
                this._rangeObjectObservers.set(
                    range,
                    this.bindingEngine.propertyObserver(range, "from").subscribe(() => this.notifyRangeChanged(true))
                );
                this._rangeObjectObservers.set(
                    range,
                    this.bindingEngine.propertyObserver(range, "to").subscribe(() => this.notifyRangeChanged(true))
                );
                this._rangeObjectObservers.set(
                    range,
                    this.bindingEngine
                        .propertyObserver(range, "isSelected")
                        .subscribe(() => this.notifyRangeChanged(true))
                );
            }
        });
    }

    // The observers that notify users of this class of changes in the ranges.
    private _rangeSubscriptions: ((rangePropertiesChanged: boolean) => void)[] = [];

    /**
     * Add an observer to changes to the array of ranges itself, or to the properties of the ranges.
     * @param callback The function to call when the ranges change, rangePropertiesChanged will be true if the properties of the ranges changed.
     * @returns A disposable that can be used to unsubscribe from the changes.
     */
    public subscribeToRangeChanges(callback: (rangePropertiesChanged: boolean) => void): Disposable {
        // Ensure that  _rangeSubscriptions is always initialized.
        if (!Array.isArray(this._rangeSubscriptions)) {
            this._rangeSubscriptions = [];
        }

        this._rangeSubscriptions.push(callback);

        return {
            dispose: () => {
                this._rangeSubscriptions = this._rangeSubscriptions.filter((o) => o != callback);
            },
        };
    }

    private notifyRangeChanged(rangePropertiesChanged = false): void {
        this.updateSelectedFrequency();
        if (Array.isArray(this._rangeSubscriptions)) {
            this._rangeSubscriptions.forEach((sub) => sub(rangePropertiesChanged));
        }
    }

    // #EndRegion
}
